import { NavLink } from 'react-router-dom';
import { useState, type ReactNode } from 'react';
import { ChatWidget } from './ChatWidget';
import { useThemeChoice } from '../hooks/useThemeChoice';
import type { ThemeChoice } from '../lib/theme';

/**
 * The application footer: a permanent strip along the bottom of every
 * authenticated page.
 *
 * WHY A FOOTER AND NOT FLOATING BUTTONS: a floating bubble covers whatever
 * is underneath it - it was sitting on top of the calendar's analytics
 * card. The footer is part of the LAYOUT, not an overlay: it takes its own
 * 44px, so nothing is ever hidden behind it, and it is in the same place on
 * every page whatever is scrolled.
 *
 * SHAPE: workspace entry points on the left (icon over label, the way a
 * chat client docks its Chats / Channels / Threads), utilities on the right
 * as quiet icon-only buttons. That split is the whole convention - a new
 * item goes left if it opens a place you work in, right if it changes how
 * the app looks or behaves.
 *
 * ADDING ORG-WIDE INTERNAL CHAT LATER: the panels for My Pins, Chats,
 * Channels, Threads and People behave exactly like the assistant's - one
 * open at a time, anchored above this bar. Add the key to `FooterPanel`,
 * push a <FooterItem> into the left group with
 * `active={panel === 'chats'}`, and render the panel next to
 * <ChatWidget>. The single `panel` state is what enforces one-at-a-time,
 * which is what stops two panels from stacking in the same corner. Unread
 * counts have a slot already: <FooterItem badge={n}>.
 */

/** One open panel at a time. Future: | 'pins' | 'chats' | 'channels' | 'threads' | 'people'. */
type FooterPanel = 'assistant' | null;

/**
 * Left group: opens a place you work in. Icon over label, like the nav it
 * will become.
 *
 * TWO FLAVOURS, ONE LOOK: pass `onClick` for an item that toggles a panel
 * (Assistant), or `to` for one that navigates (Settings). The classes live
 * in `itemClass` and are shared, so a routed item is pixel-identical to a
 * panel item - including the top border that marks the active one. Never
 * hand-roll a NavLink in the bar: that is how Settings ended up looking
 * like a different control than the Assistant beside it.
 */
const itemClass = (active?: boolean) =>
  [
    'relative flex h-full w-[68px] flex-col items-center justify-center gap-0.5 border-t-2 px-1 transition',
    active
      ? 'border-brand-500 text-brand-700'
      : 'border-transparent text-slate-500 hover:bg-slate-100 hover:text-slate-900',
  ].join(' ');

function FooterItemBody({ icon, label, badge }: { icon: ReactNode; label: string; badge?: number }) {
  return (
    <>
      <span className="relative">
        {icon}
        {badge !== undefined && badge > 0 && (
          <span className="absolute -right-2 -top-1 flex h-3.5 min-w-3.5 items-center justify-center rounded-full bg-red-600 px-1 text-[9px] font-semibold text-white">
            {badge > 9 ? '9+' : badge}
          </span>
        )}
      </span>
      <span className="text-[10px] leading-none">{label}</span>
    </>
  );
}

function FooterItem({
  label, icon, onClick, active, badge, to,
}: {
  label: string;
  icon: ReactNode;
  onClick?: () => void;
  active?: boolean;
  badge?: number;
  /** When set the item navigates; `active` then comes from the route. */
  to?: string;
}) {
  if (to) {
    return (
      <NavLink to={to} title={label} className={({ isActive }) => itemClass(isActive)}>
        <FooterItemBody icon={icon} label={label} badge={badge} />
      </NavLink>
    );
  }
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      title={label}
      className={itemClass(active)}
    >
      <FooterItemBody icon={icon} label={label} badge={badge} />
    </button>
  );
}

/** Right group: utilities. Icon only - the tooltip and aria-label carry the name. */
function FooterTool({
  label, onClick, children,
}: {
  label: string;
  onClick: () => void;
  children: ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      title={label}
      aria-label={label}
      className="flex h-full w-10 items-center justify-center text-slate-500 transition hover:bg-slate-100 hover:text-slate-900"
    >
      {children}
    </button>
  );
}

/** light -> dark -> system -> light. Three states, one button. */
const NEXT: Record<ThemeChoice, ThemeChoice> = {
  light: 'dark',
  dark: 'system',
  system: 'light',
};

const THEME_LABEL: Record<ThemeChoice, string> = {
  light: 'Light',
  dark: 'Dark',
  system: 'System',
};

function AppearanceTool() {
  const { choice, dark, setChoice } = useThemeChoice();
  return (
    <FooterTool
      label={`Appearance: ${THEME_LABEL[choice]} - switch to ${THEME_LABEL[NEXT[choice]]}`}
      onClick={() => setChoice(NEXT[choice])}
    >
      {/* The icon shows what is ON SCREEN; 'system' is marked with a dot so
          the two automatic states are not mistaken for a manual choice. */}
      <span className="relative">
        {dark ? (
          <svg viewBox="0 0 20 20" fill="none" aria-hidden="true" className="h-[18px] w-[18px]">
            <path d="M16 12.3A6.5 6.5 0 0 1 7.7 4a6.5 6.5 0 1 0 8.3 8.3z" fill="currentColor" />
          </svg>
        ) : (
          <svg viewBox="0 0 20 20" fill="none" aria-hidden="true" className="h-[18px] w-[18px]">
            <circle cx="10" cy="10" r="3.6" fill="currentColor" />
            <path
              d="M10 1.6v2M10 16.4v2M1.6 10h2M16.4 10h2M4.1 4.1l1.4 1.4M14.5 14.5l1.4 1.4M15.9 4.1l-1.4 1.4M5.5 14.5l-1.4 1.4"
              stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"
            />
          </svg>
        )}
        {choice === 'system' && (
          <span
            aria-hidden="true"
            className="absolute -bottom-1 left-1/2 h-1 w-1 -translate-x-1/2 rounded-full bg-current"
          />
        )}
      </span>
    </FooterTool>
  );
}

/**
 * The platform console reuses this bar, which means two things cannot be
 * assumed any more:
 *
 *   - WHERE SETTINGS GOES. '/settings' is a tenant route behind
 *     ProtectedRoute; an operator clicking it would be bounced to the
 *     portal login, which looks like being signed out.
 *   - WHETHER THE ASSISTANT BELONGS. ChatWidget calls tenant endpoints
 *     with the tenant token. There is no tenant session in the console, so
 *     it would 401 on every message.
 *
 * Both are props rather than a `useLocation()` check inside the component:
 * a bar that silently behaves differently depending on the URL is harder
 * to reason about than one told what it is.
 */
export function AppFooter({
  settingsTo = '/settings',
  showAssistant = true,
}: {
  settingsTo?: string;
  showAssistant?: boolean;
} = {}) {
  const [panel, setPanel] = useState<FooterPanel>(null);
  const toggle = (next: Exclude<FooterPanel, null>) =>
    setPanel((current) => (current === next ? null : next));

  return (
    <>
      {/*
        Panels live outside the bar so the bar stays a thin strip of
        controls. ChatWidget stays mounted while closed - that is what
        keeps the conversation alive across open/close.
      */}
      {showAssistant && (
        <ChatWidget open={panel === 'assistant'} onClose={() => setPanel(null)} />
      )}

      <footer className="flex h-11 shrink-0 items-stretch justify-between border-t border-slate-200 bg-white">
        <div className="flex items-stretch">
          <AppearanceTool />
        </div>
         <div className="flex items-stretch">
          {/* Settings moved here when the module rail landed: it belongs to
              no single module, and a seventh rail icon for one page would
              have made the map worse. It sits with the other
              app-wide utilities. */}
          <FooterItem
            label="Settings"
            to={settingsTo}
            icon={
              <svg viewBox="0 0 20 20" fill="none" aria-hidden="true" className="h-[18px] w-[18px]">
                <path
                  d="M10 13a3 3 0 100-6 3 3 0 000 6zM10 2v2m0 12v2M4.2 4.2l1.4 1.4m8.8 8.8l1.4 1.4M2 10h2m12 0h2M4.2 15.8l1.4-1.4m8.8-8.8l1.4-1.4"
                  stroke="currentColor"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            }
          />
          {showAssistant && (
          <FooterItem
            label="Assistant"
            active={panel === 'assistant'}
            onClick={() => toggle('assistant')}
            icon={
              <svg viewBox="0 0 20 20" fill="none" aria-hidden="true" className="h-[18px] w-[18px]">
                <path
                  d="M10 3c-3.9 0-7 2.6-7 5.9 0 1.9 1 3.5 2.6 4.6L5 17l3.2-1.5c.6.1 1.2.2 1.8.2 3.9 0 7-2.6 7-5.9S13.9 3 10 3z"
                  fill="currentColor"
                />
              </svg>
            }
          />
          )}
          {/* Chats / Channels / Threads / People land here - see the note above. */}
        </div>
      </footer>
    </>
  );
}
